import { Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Modal, Paper, Select, Typography } from "@material-ui/core";
import { getPricingVisualizer } from "../../../Actions";
import { CalculationVisualizerContext } from "../../../Context/CalculationVisualizerContextProvider";
import { useContext, useEffect, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";

export default function FlowModal (props) {
  const { isPromoFlow, setIsPromoFlow, selectedOrientation } = useContext(CalculationVisualizerContext);
  const [nodes, setNodes] = useState([]);
  // const [edges, setEdges] = useState([]);
  // const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(props?.roleId || "");
  const [productUUID, setProductUUID] = useState(props?.productUUID || "");
  // Preselect the option the caller was looking at (e.g. the row whose "?"
  // was clicked on the product search page); fall back to the first option.
  const [selectedOption, setSelectedOption] = useState(
    (props.initialCostId != null && props.options?.find((o) => o.cost_id === props.initialCostId)) ||
      props.options[0] ||
      null
  );

  // The modal stays mounted while the page switches roles, so props change
  // under it — initial useState alone goes stale (the role-switch bug).
  useEffect(() => {
    setSelectedRole(props?.roleId || "");
  }, [props.roleId]);

  // Role switches refetch the page's options; re-resolve the selection from
  // the fresh list (same cost_id when it survives, else the caller's
  // initial option, else the first) so labels and pricing stay current.
  useEffect(() => {
    const fresh =
      props.options?.find((o) => o.cost_id === selectedOption?.cost_id) ||
      (props.initialCostId != null && props.options?.find((o) => o.cost_id === props.initialCostId)) ||
      props.options?.[0] ||
      null;
    setSelectedOption(fresh ? { ...fresh } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.options]);

  // Re-opening from a "?" lands on the option the user was looking at.
  useEffect(() => {
    if (!props.isOpen || props.initialCostId == null) return;
    const match = props.options?.find((o) => o.cost_id === props.initialCostId);
    if (match) setSelectedOption({ ...match });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);
  const [isPromoFlowAvailable, setIsPromoFlowAvailable] = useState(false);
  // Raw step-eval JSON for troubleshooting — toggled by the Debug button,
  // visible to hidden admins only.
  const [showDebug, setShowDebug] = useState(false);

  useEffect(() => {
    if (!props?.isOpen) setShowDebug(false);
  }, [props.isOpen]);

  async function getDataWithCalculations(productUUID, selectedRole, selectedOption, selectedOrientation) {
    await getPricingVisualizer(productUUID, selectedRole, selectedOption, selectedOrientation)
      .then((result) => {
        const { nodes: newNodes, edges: newEdges } = result.data;

        setNodes([ ...newNodes ]);
        // setEdges([ ...newEdges ]);
      })
      .catch((err) => {
        setNodes([]);
        // setEdges([]);
      });
  }

  useEffect(() => {
    setNodes([]);
    // setEdges([]);

    if (!props?.isOpen) return;
    if (!selectedOption?.cost_id || !productUUID || !selectedRole) return;

    getDataWithCalculations(productUUID, selectedRole, selectedOption?.cost_id, selectedOrientation);
  }, [props.isOpen, selectedOption?.cost_id, selectedOrientation, selectedRole])

  useEffect(() => {
    if (nodes?.[0]?.data?.nodeData?.pricingData?.promotion) {
      setIsPromoFlowAvailable(true);
      return;
    }

    setIsPromoFlowAvailable(false);
    setIsPromoFlow(false);
  }, [nodes])

  const fmtMoney = (v) => {
    const n = Number(v);
    return isNaN(n) ? null : n.toFixed(2);
  };

  const pctLabel = (v) => {
    const n = Number(v) * 100;
    return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  };

  // Plain-English description of a formula step, e.g.
  // profitMargin 0.47 -> "Applied 47% gross margin (÷ 0.53)".
  function describeFormula(formula, valueAdded, attribute, formulaEval) {
    // Unit-conversion steps include their UOM pair, e.g. "(unit conversion factor SF → SY)".
    const uomPair =
      formulaEval?.sourceUom && formulaEval?.targetUom
        ? ` ${formulaEval.sourceUom} → ${formulaEval.targetUom}`
        : '';
    const fromAttr = attribute ? ` (${String(attribute).replace(/_/g, ' ')}${uomPair})` : '';

    switch (formula) {
      case 'add': return `Added $${fmtMoney(valueAdded)}${fromAttr}`;
      case 'subtract': return `Subtracted $${fmtMoney(valueAdded)}${fromAttr}`;
      case 'multiply': return `Multiplied by ${valueAdded}${fromAttr}`;
      case 'divide': return `Divided by ${valueAdded}${fromAttr}`;
      case 'percentageIncrease': return `Increased by ${pctLabel(valueAdded)}${fromAttr}`;
      case 'percentageDecrease': return `Decreased by ${pctLabel(valueAdded)}${fromAttr}`;
      case 'profitMargin': return `Applied ${pctLabel(valueAdded)} gross margin (÷ ${(1 - Number(valueAdded)).toFixed(2)})`;
      case 'retailRoundUp': return `Rounded up to a ${Math.round(Number(valueAdded) * 100)}¢ retail ending`;
      default: return `Applied ${formula} with ${valueAdded}`;
    }
  }

  // Flattens the taken calculation path into receipt lines: the starting
  // cost, one line per formula step that changed the amount (what changed,
  // by how much, running total), then the final price. Nodes that didn't
  // move the number — branch decisions, which only pick a path, and no-op
  // formula steps — are left out. When the price is overridden only
  // start/end are kept — the intermediate math was computed but discarded,
  // and showing it would mislead.
  function buildReceiptLines(allNodes, pricingType) {
    const path = (allNodes || [])
      .filter((n) => n.data.nodeData.pricingData)
      .sort((a, b) => a.level.x - b.level.x);

    const lines = [];
    let running = null;
    let overridden = false;

    path.forEach((n) => {
      const pd = n.data.nodeData.pricingData[pricingType];

      if (!pd) return;
      if (pd.isPriceOverriden) overridden = true;

      if (n.data.id === 'start') {
        if (pd.value === null || pd.value === undefined) return;
        running = Number(pd.value);
        lines.push({ key: n.id, type: 'start', label: 'Starting cost', amount: running });
        return;
      }

      if (n.data.id === 'end') {
        if (pd.value === null || pd.value === undefined) return;
        lines.push({ key: n.id, type: 'end', label: 'Customer price', amount: Number(pd.value) });
        return;
      }

      if (overridden) return;

      if (pd.conditionEval) {
        // Branch decisions never change the amount — track the running value
        // and move on without a line.
        if (pd.conditionEval.value !== null && pd.conditionEval.value !== undefined) {
          running = Number(pd.conditionEval.value);
        }
        return;
      }

      if (pd.formulaEval?.wereValuesUsed) {
        const result = Number(pd.formulaEval.result);
        const delta = running !== null && !isNaN(result) ? result - running : null;

        // A step that left the amount unchanged (e.g. a round-up that had
        // nothing to round) adds nothing to the story.
        if (delta !== null && Math.abs(delta) < 0.005) {
          running = result;
          return;
        }

        lines.push({
          key: n.id,
          type: 'step',
          label: n.data.label,
          desc: describeFormula(n.data.nodeData.formula || pd.formulaEval.formula, pd.formulaEval.valueAdded, n.data.nodeData.attribute, pd.formulaEval),
          delta,
          amount: result,
        });
        running = result;
      }
    });

    return lines;
  }

  return (
    <Modal
      open={props?.isOpen || false}
      onClose={props?.onClose}
      aria-labelledby="calculation flow modal"
      aria-describedby="modal to show the steps for a calculation flow"
      style={{marginTop: '100px'}}
    >
      <Paper style={{padding: "20px 15px", position: "absolute", top: "50%", left: "50%", width: "80vw", maxHeight: "800px", maxWidth: "600px", transform: `translate(-50%, -50%)`}}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl size="small" variant="filled" style={{width: "100%", maxWidth: "300px"}}>
              <InputLabel shrink id="option-list-label">Selected Option</InputLabel>
              <Select
                value={selectedOption?.cost_id ?? ""}
                onChange={(e) => {
                  const option = props.options?.find(option => option.cost_id === e.target.value);

                  setSelectedOption({ ...option });
                }}        
                variant="filled"
                displayEmpty
              >
                <MenuItem value={""} disabled>
                  Please select one of the following options
                </MenuItem> 
                { 
                  props.options?.map(option => {

                    let optionDetails = "";

                    if (option.retail_price) {
                      optionDetails = "$" + option.retail_price?.toFixed(2);
                    }

                    if (option.is_promo_active) {
                      optionDetails = "$" + option.promotion_pricing.retail_price?.toFixed(2) + " (Promo)";
                    }

                    if (option.is_discontinued) {
                      optionDetails = "Discontinued";
                    } else if (option.is_ask_for_price) {
                      optionDetails = "Ask For Price";
                    } else if (option.is_call_for_price) {
                      optionDetails = "Call For Price";
                    }

                    if (option.uom) {
                      optionDetails += optionDetails !== "" ? ` - ${option.uom}` : option.uom;
                    }

                    if (option.basis_cd) {
                      optionDetails += optionDetails !== "" ? ` - ${option.basis_cd}` : option.basis_cd;
                    }

                    return (
                      <MenuItem key={option.cost_id} value={option.cost_id}>
                        <Typography variant="body2" color={option.is_discontinued ? "error" : "inherit"}>
                          <b>{option.color_name}</b>
                          <br></br>
                          {option.distributor}
                          <br></br>
                          <span style={{color: option.is_promo_active ? "#f44336" : "inherit"}}>{optionDetails}</span>
                        </Typography>
                      </MenuItem>
                    )
                  })
                }
              </Select>
            </FormControl>
          </Grid>
          { isPromoFlowAvailable && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                value={isPromoFlow?.toString()}
                exclusive
                size='small'
                onChange={(e, newValue) => {
                  if (newValue === null) return;

                  setIsPromoFlow(JSON.parse(newValue))
                }}
                aria-label="text alignment"
              >
                <ToggleButton value='false' aria-label="Standard" style={!isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Standard
                </ToggleButton>
                <ToggleButton value='true' aria-label="Promotion" style={isPromoFlow ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
                  Promotion
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}
          {
            nodes?.[0]?.data?.nodeData?.pricingData?.[isPromoFlow ? 'promotion' : 'standard']?.isPriceOverriden ? (
              <Grid item xs={12}>
                <Typography variant="caption" component="div">
                  This price has an override present and therefore does not go through the full calculation flow. This override is present due to one of the following cases
                  <Typography variant="inherit" component="ul" style={{marginTop: "1em"}}>
                    <Typography variant="inherit" component="li">You are importing pricing data from an ERP system and are using the calculations from that system</Typography>
                    <Typography variant="inherit" component="li">You have set this in a manual import file</Typography>
                    <Typography variant="inherit" component="li">You have added a temporary retail price override for this option on the product search page</Typography>
                    <Typography variant="inherit" component="li">You have requested that we add a specialty override on our end</Typography>
                  </Typography>
                </Typography>
              </Grid>
            ) : undefined
          }
          <Grid item xs={12} style={{overflowY: "auto", maxHeight: "500px", marginTop: "10px"}}>
            {!nodes?.length ? (
              <Typography component="div" style={{textAlign: "center", height: "300px"}}>
                <CircularProgress size={100} style={{display: "block", margin: "50% auto 10px"}} />
                <b>Loading...</b>
              </Typography>
            ) : undefined}
            {(() => {
              if (!nodes?.length) return undefined;

              const lines = buildReceiptLines(nodes, isPromoFlow ? 'promotion' : 'standard');
              const isHiddenAdmin = props.user?.user_id && props.user?.is_hidden;
              const uomSuffix = selectedOption?.uom ? `/${selectedOption.uom}` : '';

              if (!lines.length) {
                return (
                  <Typography variant="body2" style={{ textAlign: 'center', padding: '30px 0' }}>
                    No calculation steps available for this option.
                  </Typography>
                );
              }

              const rowStyle = {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '8px 12px',
              };

              const renderLine = (line) => {
                if (line.type === 'start') {
                  return (
                    <div style={rowStyle}>
                      <Typography variant="subtitle2">{line.label}</Typography>
                      <Typography variant="subtitle2">${fmtMoney(line.amount)}</Typography>
                    </div>
                  );
                }

                if (line.type === 'end') {
                  return (
                    <div style={{ ...rowStyle, borderTop: '2px solid rgba(0,0,0,0.3)', marginTop: 4, paddingTop: 10 }}>
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{line.label}</Typography>
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                        ${fmtMoney(line.amount)}{uomSuffix}
                      </Typography>
                    </div>
                  );
                }

                const showDelta = line.delta !== null && Math.abs(line.delta) >= 0.005;

                return (
                  <div style={rowStyle}>
                    <div style={{ paddingRight: 12 }}>
                      <Typography variant="body2">{line.label}</Typography>
                      {line.desc ? (
                        <Typography variant="caption" style={{ display: 'block', color: 'rgba(0,0,0,0.6)' }}>
                          {line.desc}
                        </Typography>
                      ) : undefined}
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {showDelta ? (
                        <Typography variant="caption" style={{ display: 'block', color: 'rgba(0,0,0,0.6)' }}>
                          {line.delta >= 0 ? '+' : '−'}${fmtMoney(Math.abs(line.delta))}
                        </Typography>
                      ) : undefined}
                      <Typography variant="subtitle2">${fmtMoney(line.amount)}</Typography>
                    </div>
                  </div>
                );
              };

              return (
                <div>
                  <div style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 5, padding: '6px 0' }}>
                    {lines.map((line) => (
                      <div key={line.key}>{renderLine(line)}</div>
                    ))}
                  </div>
                  {isHiddenAdmin && showDebug ? (
                    <pre style={{ fontSize: 11, overflowX: 'auto', backgroundColor: '#00000008', borderRadius: 5, padding: 10, marginTop: 10 }}>
                      {JSON.stringify(
                        nodes
                          .filter((n) => n.data.nodeData.pricingData)
                          .sort((a, b) => a.level.x - b.level.x)
                          .map((n) => ({
                            node: n.data.id,
                            label: n.data.label,
                            eval: n.data.nodeData.pricingData[isPromoFlow ? 'promotion' : 'standard'] ?? null,
                          })),
                        null,
                        2
                      )}
                    </pre>
                  ) : undefined}
                </div>
              );
            })()}
          </Grid>
          <Grid item xs={12}>
            {props.user?.user_id && props.user?.is_hidden ? (
              <Button size="small" onClick={() => setShowDebug((v) => !v)} style={{ marginTop: '10px', color: 'rgba(0,0,0,0.4)' }}>
                {showDebug ? 'Hide debug' : 'Debug'}
              </Button>
            ) : undefined}
            <Button variant="contained" onClick={props.onClose} style={{marginTop: "10px", float: "right"}}>
              Close
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  )
}